import { Box, Button, Icon, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import React, { useCallback, useMemo, useState } from 'react';

import { firstValueFrom } from 'rxjs';

import { PaginatedDataGrid } from 'app/components/paginated-data-grid';
import { GeneralConstants } from 'app/constants/general-constants';
import { MlSampleMetadata, RestfulModel } from 'app/model';
import { messagingService, RestfulApiService } from 'app/services';
import { dialogService } from 'app/services/dialog.service';
import { mlSampleService } from 'app/services/machine-learning/ml-sample.service';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

export const MLSamples = (): React.ReactElement => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedSamples, setSelectedSamples] = useState<GridRowSelectionModel>([]);

    const handleDelete = useCallback((sample: MlSampleMetadata) => {
        dialogService.showConfirm(
            {
                message: 'Are you sure you want to delete this sample?',
                confirmText: 'Delete',
                cancelText: 'Cancel',
            },
            () => {
                mlSampleService.delete(sample).subscribe({
                    next: () => {
                        messagingService.showSuccess('Sample deleted successfully');
                        setRefreshKey((prev) => prev + 1);
                    },
                    error: (err: Error) => {
                        messagingService.showError(`Failed to delete sample: ${err.message}`);
                    },
                });
            },
        );
    }, []);

    const handleSelectionChange = useCallback((selectionModel: GridRowSelectionModel) => {
        setSelectedSamples(selectionModel);
    }, []);

    const confirmAndMergeSamples = useCallback(() => {
        dialogService.showConfirm(
            {
                message: 'Are you sure you want to merge the selected samples?',
                confirmText: 'Merge',
                cancelText: 'Cancel',
            },
            () => {
                const [targetId, ...otherIds] = selectedSamples;
                mlSampleService.mergeSamples(Number(targetId), otherIds.map(Number)).subscribe({
                    next: () => {
                        messagingService.showSuccess('Samples merged successfully');
                        setRefreshKey((prev) => prev + 1);
                    },
                    error: () => {
                        messagingService.showError(`Failed to merge samples`);
                    },
                });
            },
        );
    }, [selectedSamples]);

    const handleMergeSamples = useCallback(() => {
        if (1 < selectedSamples.length) {
            confirmAndMergeSamples();
        }
    }, [selectedSamples, confirmAndMergeSamples]);

    const columns = useMemo<Array<GridColDef<MlSampleMetadata>>>(
        () => [
            {
                field: 'actions',
                headerName: 'Actions',
                width: 100,
                type: 'actions',
                getActions: (params) => [
                    <GridActionsCellItem
                        icon={<Icon>delete</Icon>}
                        label='Delete'
                        onClick={() => handleDelete(params.row)}
                    />,
                ],
            },
            {
                field: 'id',
                headerName: 'ID',
                width: 100,
                valueFormatter: DataGridFormatters.numberLocaleFormatter,
            },
            {
                field: 'name',
                headerName: 'Name',
                width: 200,
                editable: true,
            },
            {
                field: 'elasticIndex',
                headerName: 'Elastic Index',
                width: 200,
            },
            {
                field: 'sampleStatus',
                headerName: 'Status',
                width: 120,
                valueFormatter: (params) => (0 === params.value ? 'Pending' : 'Complete'),
            },
            {
                field: 'dateCreated',
                headerName: 'Created Date',
                width: 200,
                valueFormatter: DataGridFormatters.dateTimeFormatter,
            },
        ],
        [handleDelete],
    );

    const handleRowUpdate = async (newRow: MlSampleMetadata): Promise<MlSampleMetadata> => {
        return firstValueFrom(mlSampleService.putAction(newRow));
    };

    const memoizedGrid = useMemo(
        () => (
            <PaginatedDataGrid<MlSampleMetadata>
                key={refreshKey}
                dataSide='client'
                autoHeight
                density='compact'
                columns={columns}
                service={mlSampleService as unknown as RestfulApiService<RestfulModel, unknown>}
                listOptions={{
                    pageSize: GeneralConstants.PAGE_SIZE_HUGE,
                }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'dateCreated', sort: 'desc' }],
                    },
                }}
                checkboxSelection
                disableRowSelectionOnClick={false}
                onRowSelectionModelChange={handleSelectionChange}
                rowSelectionModel={selectedSamples}
                processRowUpdate={handleRowUpdate}
            />
        ),
        [columns, refreshKey, selectedSamples, handleSelectionChange],
    );

    return (
        <>
            <Typography variant='h4'>ML Samples</Typography>
            <Box display='flex' flexDirection='column' gap={2} sx={{ mt: 2 }}>
                <Box display='flex' alignItems='center' gap={2}>
                    <Typography variant='body1'>View and manage machine learning samples</Typography>
                    <Button
                        variant='contained'
                        color='primary'
                        disabled={1 >= selectedSamples.length}
                        onClick={handleMergeSamples}
                    >
                        Merge Samples
                    </Button>
                </Box>
                <Typography variant='body1'>
                    Double click on sample name to edit it (or select cell and press enter)
                </Typography>
                <Box>{memoizedGrid}</Box>
            </Box>
        </>
    );
};
